import { handleActions } from 'redux-actions';
import { DamzState, FocusMessageStep, RoomFlagNames, InitializationStatus } from '../../types';
import {
  closeRoom,
  incrementMessageSent,
  markRoomRead,
  markRoomReadSyncDone,
  presenceJoin,
  presenceLeave,
  reloadActiveRoom,
  requestFocusOnMessage,
  scrollToRequestedMessage,
  selectRoom,
  sendMessageFailed,
  clearSendError,
  setActiveRoomData,
  setMessageSending,
  setTypingText,
  stopTyping,
  toggleStarred,
  toggleMuted,
  toggleArchived,
  toggleStartNewMessage,
  closeNewChat,
  waitForRenderBeforeScrollingToMessage,
  fetchActiveParticipantsSuccess,
  openRoomParticipantsList,
  closeRoomParticipantsList,
  scrollToRequestedMessageDoneScrolling,
  scrollToRequestedMessageCleanupSuccess,
  fetchDmCardData,
  fetchDmCardDataSuccess,
  fetchDmCardDataFailure,
  markAsRead,
  markAsUnread,
  unsetRequestFlag,
  clearActiveRoomItem,
  deleteLeaveRoomSuccess,
} from '../actions/room';
import { checkRoomFlag, setRoomFlag, unsetRoomFlag } from '../../utils/roomFlags';

const defaultFocusMessageState: DamzState['room']['focusMessageRequest'] = {
  messageId: undefined,
  requiredFetch: false,
  step: FocusMessageStep.Idle,
};

export default handleActions<DamzState['room'], any>(
  {
    [`${toggleStartNewMessage}`]: (state, action: ReturnType<typeof toggleStartNewMessage>) => ({
      ...state,
      newChatActive: !state.newChatActive,
      sendError: undefined,
      // disappear the partis list if we going to show new messages
      isShowingRoomParticipantsList: !state.newChatActive
        ? false
        : state.isShowingRoomParticipantsList,
    }),
    [`${closeNewChat}`]: (state, action: ReturnType<typeof closeNewChat>) => ({
      ...state,
      newChatActive: false,
      sendError: undefined,
      // disappear the partis list when closing new chat
      isShowingRoomParticipantsList: false,
    }),
    [`${sendMessageFailed}`]: (state, action: ReturnType<typeof sendMessageFailed>) => ({
      ...state,
      sendError: action.payload.err,
    }),
    [`${clearSendError}`]: (state, action: ReturnType<typeof clearSendError>) => ({
      ...state,
      sendError: undefined,
    }),
    [`${incrementMessageSent}`]: (state, action: ReturnType<typeof incrementMessageSent>) => ({
      ...state,
      sentMarker: state.sentMarker + 1,
    }),
    [`${selectRoom}`]: (state, action: ReturnType<typeof selectRoom>) => ({
      ...state,
      isReadyToFocusMessageBox: false,
      activeRoomId: action.payload.room_id,
      sendError: undefined,
      // can't show this open when switching to a dm from a community/multi
      isShowingRoomParticipantsList: false,
      presence: {}, // Cleaning up presence for new room
    }),
    [`${closeRoom}`]: (state, action: ReturnType<typeof closeRoom>) => ({
      ...state,
      activeRoomId: undefined,
      activeRoomRow: undefined,
      typingText: undefined,
      isShowingRoomParticipantsList: false,
      focusMessageRequest: { ...defaultFocusMessageState },
      presence: {}, // Cleaning up presence for new room
      sendError: undefined,
      justClosedRoomId: state.activeRoomId,
    }),
    [`${reloadActiveRoom}`]: (state, action: ReturnType<typeof reloadActiveRoom>) => ({
      ...state,
      sendError: undefined,
    }),
    [`${setActiveRoomData}`]: (state, action: ReturnType<typeof setActiveRoomData>) => ({
      ...state,
      isReadyToFocusMessageBox: true,
      prevActiveRoom: state.activeRoomId ?? undefined,
      activeRoomItem: action.payload.roomData,
      /**
       * Piggybacking on this action to reset the dm card data.
       */
      dmCardData: {
        initStatus: InitializationStatus.Initializing,
        watcherCount: 0,
        deviationCount: 0,
        isCurrentUserWatchingThisOne: false,
      },
    }),
    [`${markRoomRead}`]: (state, action: ReturnType<typeof markRoomRead>) => ({
      ...state,
      markAsReadQueue: [
        ...(state.markAsReadQueue ?? []),
        {
          room_id: action.payload.room_id,
          skipDecrement: action.payload.skipDecrement,
        },
      ],
    }),
    [`${markRoomReadSyncDone}`]: (state, action: ReturnType<typeof markRoomReadSyncDone>) => ({
      ...state,
      markAsReadQueue: [],
    }),
    [`${presenceJoin}`]: (state, action: ReturnType<typeof presenceJoin>) => {
      const room_id = action.payload.room_id;
      const joiners = action.payload.joiners.reduce((acc, item) => {
        acc[item.user_id] = item;
        return acc;
      }, {});
      return {
        ...state,
        presence: {
          ...state.presence,
          [room_id]: {
            ...state?.presence?.[room_id],
            ...joiners,
          },
        },
      };
    },
    [`${presenceLeave}`]: (state, action: ReturnType<typeof presenceLeave>) => {
      const { room_id: roomId, leavers } = action.payload;
      const leftUserIds = leavers.map(({ user_id }) => user_id);

      const roomPresence = state?.presence?.[roomId] ?? {};

      const updatedPresence = Object.fromEntries(
        Object.entries(roomPresence).filter(([userId]) => !leftUserIds.includes(userId))
      );

      return {
        ...state,
        presence: {
          ...state.presence,
          [roomId]: updatedPresence,
        },
      };
    },
    [`${stopTyping}`]: (state, action: ReturnType<typeof stopTyping>) => {
      return {
        ...state,
        typingText: undefined,
      };
    },
    [`${setTypingText}`]: (state, action: ReturnType<typeof setTypingText>) => {
      return {
        ...state,
        typingText: action.payload.text,
      };
    },
    [`${requestFocusOnMessage}`]: (state, action: ReturnType<typeof requestFocusOnMessage>) => ({
      ...state,
      focusMessageRequest: {
        ...defaultFocusMessageState,
        messageId: action.payload.messageId,
      },
    }),
    [`${waitForRenderBeforeScrollingToMessage}`]: (
      state,
      action: ReturnType<typeof waitForRenderBeforeScrollingToMessage>
    ) => ({
      ...state,
      focusMessageRequest: {
        ...state.focusMessageRequest,
        step: FocusMessageStep.Render,
      },
    }),
    [`${scrollToRequestedMessage}`]: (
      state,
      action: ReturnType<typeof scrollToRequestedMessage>
    ) => ({
      ...state,
      focusMessageRequest: {
        ...state.focusMessageRequest,
        requiredFetch: action.payload.requiredFetch,
        step: FocusMessageStep.Scrolling,
      },
    }),
    [`${scrollToRequestedMessageDoneScrolling}`]: state => ({
      ...state,
      focusMessageRequest: {
        ...state.focusMessageRequest,
        step: FocusMessageStep.DoneScrolling,
      },
    }),
    [`${scrollToRequestedMessageCleanupSuccess}`]: state => ({
      ...state,
      focusMessageRequest: {
        ...state.focusMessageRequest,
        requiredFetch: false,
        step: FocusMessageStep.Idle,
      },
    }),
    [`${setMessageSending}`]: (state, action: ReturnType<typeof setMessageSending>) => ({
      ...state,
      sendingMessages: {
        ...state.sendingMessages,
        [action.payload.room_id]: action.payload.isSending,
      },
    }),
    [`${toggleStarred}`]: (state, action: ReturnType<typeof toggleStarred>) => {
      if (!state.activeRoomItem) return state;
      const currentFlags = state.activeRoomItem.roomMeta.flags;
      const isCurrentlyStarred = checkRoomFlag(currentFlags, RoomFlagNames.starred);
      const newFlags = isCurrentlyStarred
        ? unsetRoomFlag(currentFlags, RoomFlagNames.starred)
        : setRoomFlag(currentFlags, RoomFlagNames.starred);
      return {
        ...state,
        activeRoomItem: {
          ...state.activeRoomItem,
          roomMeta: {
            ...state.activeRoomItem.roomMeta,
            flags: newFlags,
          },
        },
      };
    },
    [`${toggleMuted}`]: (state, action: ReturnType<typeof toggleMuted>) => {
      if (!state.activeRoomItem) return state;
      const currentFlags = state.activeRoomItem.roomMeta.flags;
      const isCurrentlyMuted = checkRoomFlag(currentFlags, RoomFlagNames.muted);
      const newFlags = isCurrentlyMuted
        ? unsetRoomFlag(currentFlags, RoomFlagNames.muted)
        : setRoomFlag(currentFlags, RoomFlagNames.muted);
      return {
        ...state,
        activeRoomItem: {
          ...state.activeRoomItem,
          roomMeta: {
            ...state.activeRoomItem.roomMeta,
            flags: newFlags,
          },
        },
      };
    },
    [`${toggleArchived}`]: (state, action: ReturnType<typeof toggleArchived>) => {
      if (!state.activeRoomItem) return state;
      const currentFlags = state.activeRoomItem.roomMeta.flags;
      const isCurrentlyArchived = checkRoomFlag(currentFlags, RoomFlagNames.archived);
      const newFlags = isCurrentlyArchived
        ? unsetRoomFlag(currentFlags, RoomFlagNames.archived)
        : setRoomFlag(currentFlags, RoomFlagNames.archived);
      return {
        ...state,
        activeRoomItem: {
          ...state.activeRoomItem,
          roomMeta: {
            ...state.activeRoomItem.roomMeta,
            flags: newFlags,
          },
        },
      };
    },
    [`${openRoomParticipantsList}`]: (
      state,
      action: ReturnType<typeof openRoomParticipantsList>
    ) => ({
      ...state,
      isShowingRoomParticipantsList: true,
    }),
    [`${closeRoomParticipantsList}`]: state => ({
      ...state,
      isShowingRoomParticipantsList: false,
    }),
    [`${fetchActiveParticipantsSuccess}`]: (
      state,
      action: ReturnType<typeof fetchActiveParticipantsSuccess>
    ) => {
      const { participants } = action.payload;
      const { activeRoomItem } = state;
      if (!activeRoomItem) {
        return state;
      }
      return {
        ...state,
        activeRoomItem: {
          ...activeRoomItem,
          participants,
          room: {
            ...activeRoomItem.room,
            user_count: participants.length,
          },
        },
      };
    },
    [`${fetchDmCardData}`]: (state, action: ReturnType<typeof fetchDmCardData>) => {
      return {
        ...state,
        dmCardData: {
          initStatus: InitializationStatus.Initializing,
          watcherCount: 0,
          deviationCount: 0,
          isCurrentUserWatchingThisOne: false,
        },
      };
    },
    [`${fetchDmCardDataSuccess}`]: (state, action: ReturnType<typeof fetchDmCardDataSuccess>) => {
      const { watcherCount, deviationCount, isWatching } = action.payload;
      return {
        ...state,
        dmCardData: {
          initStatus: InitializationStatus.Success,
          watcherCount,
          deviationCount,
          isCurrentUserWatchingThisOne: isWatching,
        },
      };
    },
    [`${fetchDmCardDataFailure}`]: (state, action: ReturnType<typeof fetchDmCardDataFailure>) => {
      return {
        ...state,
        dmCardData: {
          initStatus: InitializationStatus.Failure,
          watcherCount: 0,
          deviationCount: 0,
          isCurrentUserWatchingThisOne: false,
        },
      };
    },
    [`${markAsRead}`]: (state, action: ReturnType<typeof markAsRead>) => {
      const { room_id } = action.payload;

      // If the active room matches the room being marked as read, update the active room's flags
      if (state.activeRoomId === room_id && state.activeRoomItem) {
        const currentFlags = state.activeRoomItem.roomMeta.flags;
        const newFlags = unsetRoomFlag(currentFlags, RoomFlagNames.unread);

        return {
          ...state,
          activeRoomItem: {
            ...state.activeRoomItem,
            roomMeta: {
              ...state.activeRoomItem.roomMeta,
              flags: newFlags,
            },
          },
        };
      }

      return state;
    },
    [`${markAsUnread}`]: (state, action: ReturnType<typeof markAsUnread>) => {
      const { room_id } = action.payload;

      // If the active room matches the room being marked as unread, update the active room's flags
      if (state.activeRoomId === room_id && state.activeRoomItem) {
        const currentFlags = state.activeRoomItem.roomMeta.flags;
        const newFlags = setRoomFlag(currentFlags, RoomFlagNames.unread);

        return {
          ...state,
          activeRoomItem: {
            ...state.activeRoomItem,
            roomMeta: {
              ...state.activeRoomItem.roomMeta,
              flags: newFlags,
            },
          },
        };
      }

      return state;
    },
    [`${unsetRequestFlag}`]: (state, action: ReturnType<typeof unsetRequestFlag>) => {
      const { room_id } = action.payload;

      // If the active room matches the room being updated, unset the request flag
      if (state.activeRoomId === room_id && state.activeRoomItem) {
        const currentFlags = state.activeRoomItem.roomMeta.flags;
        const newFlags = unsetRoomFlag(currentFlags, RoomFlagNames.request);

        return {
          ...state,
          activeRoomItem: {
            ...state.activeRoomItem,
            roomMeta: {
              ...state.activeRoomItem.roomMeta,
              flags: newFlags,
            },
          },
        };
      }

      return state;
    },
    [`${clearActiveRoomItem}`]: (state, action: ReturnType<typeof clearActiveRoomItem>) => ({
      ...state,
      activeRoomItem: undefined,
    }),
    [`${deleteLeaveRoomSuccess}`]: (state, action: ReturnType<typeof deleteLeaveRoomSuccess>) => ({
      ...state,
      activeRoomId: undefined,
      activeRoomItem: undefined,
    }),
  },
  {
    isReadyToFocusMessageBox: false,
    newChatActive: false,
    activeRoomId: undefined,
    focusMessageRequest: { ...defaultFocusMessageState },
    sentMarker: 0,
    dmCardData: {
      initStatus: InitializationStatus.Initializing,
      watcherCount: 0,
      deviationCount: 0,
      isCurrentUserWatchingThisOne: false,
    },
    sendError: undefined,
    sendingMessages: {},
    isShowingRoomParticipantsList: false,
  }
);
